import type { ReactNode } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { ChevronRight, House } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMenuLabels } from '@/features/menuLabels/hooks/useMenuLabels';
import { useActiveNavTrail, type ActiveNavTrail } from './utils/nav.utils';

const CURRENT_STEP_CLASS = 'min-w-0 truncate font-semibold text-foreground';
const HOME_LINK_CLASS =
  'flex size-7 items-center justify-center rounded-lg text-muted-foreground transition-colors outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-ring/50 motion-reduce:transition-none';

function BreadcrumbSeparator() {
  return (
    <li aria-hidden className="shrink-0 text-muted-foreground/60">
      <ChevronRight className="size-3.5" />
    </li>
  );
}

interface MenuStepsProps {
  trail: ActiveNavTrail;
  isMenuPageCurrent: boolean;
}

function MenuSteps({ trail, isMenuPageCurrent }: MenuStepsProps) {
  const { labelOf } = useMenuLabels();

  return (
    <>
      <BreadcrumbSeparator />
      <li className="shrink-0 whitespace-nowrap text-muted-foreground">
        {labelOf(trail.group.key)}
      </li>
      <BreadcrumbSeparator />
      {isMenuPageCurrent ? (
        <li aria-current="page" className={CURRENT_STEP_CLASS}>
          {labelOf(trail.child.href)}
        </li>
      ) : (
        <li className="shrink-0">
          <Link
            to={trail.child.href as '/'}
            className="whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground"
          >
            {labelOf(trail.child.href)}
          </Link>
        </li>
      )}
    </>
  );
}

interface AppBreadcrumbProps {
  currentTitle: ReactNode;
  className?: string;
}

export function AppBreadcrumb({ currentTitle, className }: AppBreadcrumbProps) {
  const activeTrail = useActiveNavTrail();
  const { pathname } = useLocation();
  const isMenuPageCurrent = activeTrail !== null && pathname === activeTrail.child.href;

  return (
    <nav aria-label="Breadcrumb" className={cn('min-w-0', className)}>
      <ol className="flex min-w-0 items-center gap-1.5 text-xs-plus">
        <li className="shrink-0">
          <Link to="/" aria-label="Home" className={HOME_LINK_CLASS}>
            <House className="size-4" aria-hidden />
          </Link>
        </li>
        {activeTrail && <MenuSteps trail={activeTrail} isMenuPageCurrent={isMenuPageCurrent} />}
        {!isMenuPageCurrent && (
          <>
            <BreadcrumbSeparator />
            <li aria-current="page" className={CURRENT_STEP_CLASS}>
              {currentTitle}
            </li>
          </>
        )}
      </ol>
    </nav>
  );
}
